import React from "react";

import { makeStyles } from '@material-ui/core/styles';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from '@material-ui/core/FormControl';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormLabel from '@material-ui/core/FormLabel';
import Divider from "@material-ui/core/Divider";

import InputLabel from "components/inputfields/InputLabel";
import InputTextField from "components/inputfields/InputTextField";
import ListDropDown from "components/inputfields/ListDropDown";
import IProduct, { BatteryEnum } from "models/product";
import { IFoodlaCategoryOption } from "models/category";
import { containsBatteries } from "components/constants-ts";
import { getProductFieldVisibility } from '../utils/productFieldVisibility';
import CmrFields from "./cmr-fields";
import ReachSubstance from "./reach-substance";
import BooleanField from "components/inputfields/BooleanField";
import ThreeWaySwitch from "components/inputfields/ThreeWaySwitch";
import { DocRequirementCallout, PPWR_DOC_REQUIREMENT } from "./doc-requirements";
import { useProductValidations } from "../validation-context/ProductValidationContext";
import MissingFieldHint from "../validation-context/MissingFieldHint";
import AiFieldVerdict from "../AiFieldVerdict";

interface IQualityReviewFieldsDataProps {
  readOnly?: boolean;
  state: IProduct;
  setState: React.Dispatch<React.SetStateAction<IProduct>>;
  rootCategory: IFoodlaCategoryOption;
  userRole?: string;
  showApoteaFields?: boolean;
};

const useStyles = makeStyles({
  label: {
    color: "#000",
    lineHeight: '24px',

    '&.Mui-focused': {
      color: "#000",
    },
  },
  divider: {
    marginBottom: "10px"
  }
});

const labelStyles = {
  margin: 0,
}

const QualityReviewFieldsBase = ({ readOnly, state, setState, rootCategory, userRole, showApoteaFields = false }: IQualityReviewFieldsDataProps) => {
  const { requiredFields } = useProductValidations();
  const { sections, fields } = getProductFieldVisibility({ product: state, rootCategory, showApoteaFields });

  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;

    setState({ ...state, [name]: value });
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;

    const formattedValue = value ?  value === "true" : null;

    setState({ ...state, [name]: formattedValue });
  };

  // FC-2021: enum answers keep their code as a string; an empty value is "Ej valt".
  const handleEnumRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = event.target.value;

    setState({ ...state, [name]: value || null });
  };

  return (
    <div style={{width: '100%'}}>
      {sections.ce &&
        (
          <>
            <FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
              <InputLabel 
                heading="Är produkten CE-märkt?"
                text="Läs mer om CE-märkningen."
                link="https://www.konsumentverket.se/for-foretag/produktsakerhet/ce-markning/"
                labelStyles={labelStyles}
                required={!!requiredFields.ceMarking}
              />
              <RadioGroup
                value={state?.ceMarking}
                id="ceMarking"
                name="ceMarking"
                style={{ flexDirection: "row", gridGap: 16 }}
                onChange={handleRadioChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio color="primary" />}
                  label="Ja"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio color="primary" />}
                  label="Nej"
                />
                <FormControlLabel
                  value={null}
                  control={<Radio color="primary" />}
                  label="Ej valt"
                />
              </RadioGroup>
              <MissingFieldHint field="ceMarking" hideAiVerdict />
            </FormControl>
            <AiFieldVerdict field="ceMarking" />
            {fields.specifyCEStandards && (
              <>
                <InputLabel
                  heading="Ange mot vilka standarder/krav"
                  required={!!requiredFields.specifyCEStandards}
                  text="Här anger du vilken standard eller vilka krav din CE-märkning gäller för."
                  link="https://www.sis.se/standarder/ce-markning/"
                />
                <InputTextField
                  readOnly={readOnly}
                  state={state}
                  setState={setState}
                  inputName="specifyCEStandards"
                  value={state.specifyCEStandards}
                  placeholder="SS-EN 71-8:2018 Leksaker - Säkerhetsregler - Del 8: Aktivitetsleksaker för privat bruk"
                  onChange={handleChange}
                />
              </>
            )}
          </>

        )
      }
      {sections.packaging && (
      <FormControl disabled={readOnly} component="fieldset">
        <InputLabel 
          heading="Levereras produkten i plasttråg?"
          labelStyles={labelStyles}
          required={!!requiredFields.plasticTrayPackaging}
        />
        <RadioGroup
          value={state?.plasticTrayPackaging}
          id="plasticTrayPackaging"
          name="plasticTrayPackaging"
          style={{ flexDirection: "row", gridGap: 16 }}
          onChange={handleRadioChange}
        >
          <FormControlLabel
            value={true}
            control={<Radio color="primary" />}
            label="Ja"
          />
          <FormControlLabel
            value={false}
            control={<Radio color="primary" />}
            label="Nej"
          />
          <FormControlLabel
            value={null}
            control={<Radio color="primary" />}
            label="Ej valt"
          />
        </RadioGroup>
        <MissingFieldHint field="plasticTrayPackaging" />
      </FormControl>
      )}
      {sections.packaging && (
        <BooleanField
          readOnly={readOnly}
          name='shrinkWrapPackaging'
          state={state}
          setState={setState}
          labelOptions={{
            heading: 'Levereras produkten inplastad, i krympplast eller shrinkwrap?',
            required: !!requiredFields.shrinkWrapPackaging,
          }}
          labelStyles={{ margin: 0 }}
        />
      )}
      {sections.packaging && (
        <FormControl disabled={readOnly} component="fieldset" style={{width: "100%"}}>
          <InputLabel 
            heading="Har produkten ett bäst före datum?"
            labelStyles={labelStyles}
            required={!!requiredFields.bestBeforeDate}
          />
          <RadioGroup
            value={state?.bestBeforeDate}
            id="bestBeforeDate"
            name="bestBeforeDate"
            onChange={handleRadioChange}
            style={{ flexDirection: "row", gridGap: 16 }}
          >
            <FormControlLabel
              value={true}
              control={<Radio color="primary" />}
              label="Ja"
            />
            <FormControlLabel
              value={false}
              control={<Radio color="primary" />}
              label="Nej"
            />
            <FormControlLabel
              value={null}
              control={<Radio color="primary" />}
              label="Ej valt"
            />
          </RadioGroup>
          <MissingFieldHint field="bestBeforeDate" />
        </FormControl>
      )}

      {sections.packaging && fields.childrenUnder16 &&
        (
          <FormControl disabled={readOnly} component="fieldset" style={{width: "100%"}}>
            <InputLabel
              heading="Är produkten avsedd för barn under 16 år?"
              required={!!requiredFields.childrenUnder16}
            />
            <RadioGroup
              value={state?.childrenUnder16}
              id="childrenUnder16"
              name="childrenUnder16"
              onChange={handleRadioChange}
              style={{ flexDirection: "row", gridGap: 16 }}
            >
              <FormControlLabel
                value={true}
                control={<Radio color="primary" />}
                label="Ja"
              />
              <FormControlLabel
                value={false}
                control={<Radio color="primary" />}
                label="Nej"
              />
              <FormControlLabel
                value={null}
                control={<Radio color="primary" />}
                label="Ej valt"
              />
            </RadioGroup>
            <MissingFieldHint field="childrenUnder16" />
          </FormControl>
        )
      }
      {showApoteaFields &&(
        <>
          <h3>Ytterligare kvalitetsfrågor</h3>
          <Divider classes={{ root: classes.divider }} />
        </>
      )}

      {fields.compliancePpwrPackaging && (
        <>
          <BooleanField
            readOnly={readOnly}
            name='compliancePpwrPackaging'
            state={state}
            setState={setState}
            labelOptions={{
              heading: 'Uppfyller produktens förpackning tillämpliga krav i PPWR, Regulation (EU) 2025/40, inklusive krav på dokumentation, märkning, spårbarhet och producentansvar?',
              text: 'Leverantören ska på begäran tillhandahålla teknisk dokumentation, EU-försäkran om överensstämmelse och annan dokumentation som styrker kravuppfyllelse.',
              // Naturvårdsverket's PPWR landing page
              link: 'https://www.naturvardsverket.se/vagledning-och-stod/producentansvar/eu-forordningen-om-forpackningar-ppwr/',
              required: !!requiredFields.compliancePpwrPackaging,
            }}
            labelStyles={{ margin: 0 }}
          />
          <DocRequirementCallout requirement={PPWR_DOC_REQUIREMENT} state={state} />
        </>
      )}

      {fields.containsBattery && (
          <>
            <InputLabel
              heading="Innehåller produkten något batteri?"
              text="Om ja, välj typ. Litiumbatterier har särskilda transportkrav."
              labelStyles={labelStyles}
              required={!!requiredFields.containsBattery}
            />
            <ListDropDown
              readOnly={readOnly}
              disableLabel={true}
              displayEmpty={true}
              listData={containsBatteries}
              value={state?.containsBattery}
              targetName="containsBattery"
              handleChange={(e) => {
                const val = e.target.value as BatteryEnum;
                setState({ ...state, containsBattery: val || null });
              }}
            />
          </>
        )}

      {/* FC-1950: POPs (EU) 2019/1021 applies to goods, jewelry, toys, electrical, medical devices and chemicals. */}
      <>
          {fields.compliancePops && (<BooleanField
            readOnly={readOnly}
            name='compliancePops'
            state={state}
            setState={setState}
            labelOptions={{ 
              heading: 'Uppfyller produkten de krav som finns i förordning (EU) 2019/1021 om långlivade organiska föroreningar (POPs)?',
              text: 'POPs-förordningen reglerar långlivade organiska föroreningar — kemikalier som är giftiga, persistenta och bioackumuleras i ekosystem och levande organismer.',
              link: 'https://www.kemi.se/lagar-och-regler/lagstiftningar-inom-kemikalieomradet/eu-gemensam-lagstiftning/langlivade-organiska-fororeningar---pop/kort-om-pops-forordningen',
              required: !!requiredFields.compliancePops,
            }}
            labelStyles={{ margin: 0 }}
          />)}
          {fields.containsBannedSubstancesPops && (<BooleanField
            readOnly={readOnly}
            name='containsBannedSubstancesPops'
            state={state}
            setState={setState}
            labelOptions={{ 
              heading: 'Innehåller produkten något av de förbjudna ämnen som är upptagna i bilaga I i förordning (EU) 2019/1021?',
              text: 'Bilaga I listar ämnen som är förbjudna att tillverka, släppa ut på marknaden eller använda — t.ex. vissa flamskyddsmedel och PFAS-ämnen.',
              link: 'https://www.kemi.se/lagar-och-regler/lagstiftningar-inom-kemikalieomradet/eu-gemensam-lagstiftning/langlivade-organiska-fororeningar---pop/kort-om-pops-forordningen',
              required: !!requiredFields.containsBannedSubstancesPops,
            }}
            labelStyles={{ margin: 0 }}
          />)}
        </>
      {
       fields.cmrSubstance &&
        <CmrFields readOnly={readOnly} state={state} setState={setState} showSubstances={!!fields.CMRSubstances} />
      }

      { 
        fields.reachSubstance && (
          <ReachSubstance
            readOnly={readOnly}
            state={state}
            setState={setState}
            rootCategory={rootCategory}
          />
        )
      }

      {fields.qrRelevantFeedCertifications && (
        <>
        <FormControl disabled={readOnly} component="fieldset" style={{width: '100%', marginTop: '7px'}}>
          <FormLabel
            required={!!requiredFields.qrRelevantFeedCertifications}
            classes={{ root: classes.label }}
          > 
            Är fodret och/eller emballaget märkt med certifiering inom hållbarhet, t. ex MSC och/eller FSC?
          </FormLabel>
          <RadioGroup
            id="qrRelevantFeedCertifications"
            value={state?.qrRelevantFeedCertifications}
            name="qrRelevantFeedCertifications"
            style={{ flexDirection: "row", gridGap: 16 }}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              value={true}
              control={<Radio color="primary" />}
              label="Ja"
            />
            <FormControlLabel
              value={false}
              control={<Radio color="primary" />}
              label="Nej"
            />
            <FormControlLabel
              value={null}
              control={<Radio color="primary" />}
              label="Ej valt"
            />
          </RadioGroup>
          <MissingFieldHint field="qrRelevantFeedCertifications" />
        </FormControl>
        </>
      )}
      {fields.qrFoodHygiene && (
        <FormControl disabled={readOnly} component="fieldset" style={{width: '100%', marginTop: '7px'}}>
          {/* {setShowQrHeader(true)} */}
          <FormLabel
            required={!!requiredFields.qrFoodHygiene}
            classes={{ root: classes.label }}
          > 
            Är livsmedlet i enlighet med Förordning <a target="_blank" rel="noopener noreferrer" href="https://eur-lex.europa.eu/legal-content/SV/TXT/HTML/?uri=CELEX:02004R0852-20210324&from=EN">(EG) 852/2004</a> om livsmedelshygien?
          </FormLabel>
          <RadioGroup
            id="qrFoodHygiene"
            value={state?.qrFoodHygiene}
            name="qrFoodHygiene"
            style={{ flexDirection: "row", gridGap: 16 }}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              value={true}
              control={<Radio color="primary" />}
              label="Ja"
            />
            <FormControlLabel
              value={false}
              control={<Radio color="primary" />}
              label="Nej"
            />
            <FormControlLabel
              value={null}
              control={<Radio color="primary" />}
              label="Ej valt"
            />
          </RadioGroup>
          <MissingFieldHint field="qrFoodHygiene" />
        </FormControl>
      )}
      {fields.qrElectronicCode &&
        <FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
          {/* {setShowQrHeader(true)} */}
          <FormLabel
            required={!!requiredFields.qrElectronicCode}
            classes={{ root: classes.label }}
          > 
            I det fall varan är en elektrisk eller elektronisk produkt, uppfyller den kraven i <a target="_blank" rel="noopener noreferrer" href="https://www.kemi.se/lagar-och-regler/lagstiftningar-inom-kemikalieomradet/eu-gemensam-lagstiftning/elektrisk-och-elektronisk-utrustning---rohs">RoHS-direktivet EU/2011/65</a>?
          </FormLabel>
          <RadioGroup
            id="qrElectronicCode"
            value={state?.qrElectronicCode}
            name="qrElectronicCode"
            style={{ flexDirection: "row", gridGap: 16 }}
            onChange={handleEnumRadioChange}
          >
            <FormControlLabel
              value="YES"
              control={<Radio color="primary" />}
              label="Ja"
            />
            <FormControlLabel
              value="NO"
              control={<Radio color="primary" />}
              label="Nej"
            />
            <FormControlLabel
              value="NOT_ELECTRICAL"
              control={<Radio color="primary" />}
              label="Ej elektrisk"
            />
            <FormControlLabel
              value={null}
              control={<Radio color="primary" />}
              label="Ej valt"
            />
          </RadioGroup>
          <MissingFieldHint field="qrElectronicCode" />
        </FormControl>
      }
      {fields.qrClassificationLabelingPackaging &&
        <FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
          {/* {setShowQrHeader(true)} */}
          <FormLabel
            required={!!requiredFields.qrClassificationLabelingPackaging}
            classes={{ root: classes.label }}
          > 
            Uppfyller produkten kraven om klassicifering, märkning och förpackning enligt CLP-förordningen <a target="_blank" rel="noopener noreferrer" href="https://www.kemi.se/lagar-och-regler/lagstiftningar-inom-kemikalieomradet/eu-gemensam-lagstiftning/clp-forordningen">(EG) 1272/2008</a>
          </FormLabel>
          <RadioGroup
            id="qrClassificationLabelingPackaging"
            value={state?.qrClassificationLabelingPackaging}
            name="qrClassificationLabelingPackaging"
            style={{ flexDirection: "row", gridGap: 16 }}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              value={true}
              control={<Radio color="primary" />}
              label="Ja"
            />
            <FormControlLabel
              value={false}
              control={<Radio color="primary" />}
              label="Nej"
            />
            <FormControlLabel
              value={null}
              control={<Radio color="primary" />}
              label="Ej valt"
            />
          </RadioGroup>
          <MissingFieldHint field="qrClassificationLabelingPackaging" />
        </FormControl>
      }
      <>
            {fields.qrLabelingSwedish && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
              {/* {setShowQrHeader(true)} */}
              <FormLabel
                required={!!requiredFields.qrLabelingSwedish}
                classes={{ root: classes.label }}
              > 
                Uppfyller märkningen kraven i den svenska <a target="_blank" rel="noopener noreferrer" href="https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/forordning-2013413-om-kosmetiska-produkter_sfs-2013-413/"> förordningen 2013:413</a> (inklusive kraven på svensk märkning)?
              </FormLabel>
              <RadioGroup
                value={state?.qrLabelingSwedish}
                id="qrLabelingSwedish"
                name="qrLabelingSwedish"
                style={{ flexDirection: "row", gridGap: 16 }}
                onChange={handleRadioChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio color="primary" />}
                  label="Ja"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio color="primary" />}
                  label="Nej"
                />
                <FormControlLabel
                  value={null}
                  control={<Radio color="primary" />}
                  label="Ej valt"
                />
              </RadioGroup>
              <MissingFieldHint field="qrLabelingSwedish" />
            </FormControl>)}
            {fields.qrMedicalClaims && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
              <FormLabel
                required={!!requiredFields.qrMedicalClaims}
                classes={{ root: classes.label }}
              > 
                Är förpackning och marknadsföring fri från <a target="_blank" rel="noopener noreferrer" href="https://www.lakemedelsverket.se/sv/kosmetika/tillverka-importera-och-salja-kosmetika/marknadsforing-av-kosmetika#hmainbody2">medicinska påståenden</a>?
              </FormLabel>
              <RadioGroup
                value={state?.qrMedicalClaims}
                id="qrMedicalClaims"
                name="qrMedicalClaims"
                style={{ flexDirection: "row", gridGap: 16 }}
                onChange={handleRadioChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio color="primary" />}
                  label="Ja"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio color="primary" />}
                  label="Nej"
                />
                <FormControlLabel
                  value={null}
                  control={<Radio color="primary" />}
                  label="Ej valt"
                />
              </RadioGroup>
              <MissingFieldHint field="qrMedicalClaims" />
            </FormControl>)}
            {fields.qrMarketingClaimsCosmetic && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
              <FormLabel
                required={!!requiredFields.qrMarketingClaimsCosmetic}
                classes={{ root: classes.label }}
              > 
                Är förpackning och marknadsföring fri från påståenden (t.ex. "paraben-fri") i enlighet med <a target="_blank" rel="noopener noreferrer" href="https://ec.europa.eu/docsroom/documents/24847">technical document on cosmetic claims</a>?
              </FormLabel>
              <RadioGroup
                value={state?.qrMarketingClaimsCosmetic}
                id="qrMarketingClaimsCosmetic"
                name="qrMarketingClaimsCosmetic"
                style={{ flexDirection: "row", gridGap: 16 }}
                onChange={handleRadioChange}
              >
                <FormControlLabel
                  value={true}
                  control={<Radio color="primary" />}
                  label="Ja"
                />
                <FormControlLabel
                  value={false}
                  control={<Radio color="primary" />}
                  label="Nej"
                />
                <FormControlLabel
                  value={null}
                  control={<Radio color="primary" />}
                  label="Ej valt"
                />
              </RadioGroup>
              <MissingFieldHint field="qrMarketingClaimsCosmetic" />
            </FormControl>)}
          </>
      <>
          {fields.qrAdditivesInFeed && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
            {/* {setShowQrHeader(true)} */}
            <FormLabel
              required={!!requiredFields.qrAdditivesInFeed}
              classes={{ root: classes.label }}
            > 
              Är fodret märkt enligt EU:s förordning om fodertillsatser – ("tillsatsförordningen", förordning <a target="_blank" rel="noopener noreferrer" href="https://eur-lex.europa.eu/legal-content/SV/TXT/PDF/?uri=CELEX:32003R1831&from=ET">(EG) 1831/2003</a>)?
            </FormLabel>
            <RadioGroup
              id="qrAdditivesInFeed"
              value={state?.qrAdditivesInFeed}
              name="qrAdditivesInFeed"
              style={{ flexDirection: "row", gridGap: 16 }}
              onChange={handleRadioChange}
            >
              <FormControlLabel
                value={true}
                control={<Radio color="primary" />}
                label="Ja"
              />
              <FormControlLabel
                value={false}
                control={<Radio color="primary" />}
                label="Nej"
              />
              <FormControlLabel
                value={null}
                control={<Radio color="primary" />}
                label="Ej valt"
              />
            </RadioGroup>
            <MissingFieldHint field="qrAdditivesInFeed" />
          </FormControl>)}
          {fields.qrFeedLabeling && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
            <FormLabel
              required={!!requiredFields.qrFeedLabeling}
              classes={{ root: classes.label }}
            > 
              Är fodret märkt enligt FEDIAF Code of Good Labelling Practice for Pet Food?
            </FormLabel>
            <RadioGroup
              id="qrFeedLabeling"
              value={state?.qrFeedLabeling}
              name="qrFeedLabeling"
              style={{ flexDirection: "row", gridGap: 16 }}
              onChange={handleRadioChange}
            >
              <FormControlLabel
                value={true}
                control={<Radio color="primary" />}
                label="Ja"
              />
              <FormControlLabel
                value={false}
                control={<Radio color="primary" />}
                label="Nej"
              />
              <FormControlLabel
                value={null}
                control={<Radio color="primary" />}
                label="Ej valt"
              />
            </RadioGroup>
            <MissingFieldHint field="qrFeedLabeling" />
          </FormControl>)}
        </>
      {fields.qrMisleadingSalesTerms && (
        <FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
          {/* {setShowQrHeader(true)} */}
          <FormLabel
            required={!!requiredFields.qrMisleadingSalesTerms}
            classes={{ root: classes.label }}
          > 
            Är förpackning och produktbeskrivning fri från säljande uttryck <a target="_blank" rel="noopener noreferrer" href="https://kontrollwiki.livsmedelsverket.se/artikel/36/saljande-uttryck">säljande uttryck</a> som kan betraktas som vilseledande?
          </FormLabel>
          <RadioGroup
            id="qrMisleadingSalesTerms"
            value={state?.qrMisleadingSalesTerms}
            name="qrMisleadingSalesTerms"
            style={{ flexDirection: "row", gridGap: 16 }}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              value={true}
              control={<Radio color="primary" />}
              label="Ja"
            />
            <FormControlLabel
              value={false}
              control={<Radio color="primary" />}
              label="Nej"
            />
            <FormControlLabel
              value={null}
              control={<Radio color="primary" />}
              label="Ej valt"
            />
          </RadioGroup>
          <MissingFieldHint field="qrMisleadingSalesTerms" />
        </FormControl>
      )}

      <>
          {/* {setShowQrHeader(true)} */}
          {fields.qrNutritionClaimsCode && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
            <FormLabel
              required={!!requiredFields.qrNutritionClaimsCode}
              classes={{ root: classes.label }}
            > 
              Är eventuella näringspåståenden i enlighet med villkoren i förordning <a target="_blank" rel="noopener noreferrer" href="https://kontrollwiki.livsmedelsverket.se/artikel/471/naringspastaenden">(EG) 1924/2006</a>?
            </FormLabel>
            <RadioGroup
              id="qrNutritionClaimsCode"
              value={state?.qrNutritionClaimsCode}
              name="qrNutritionClaimsCode"
              style={{ flexDirection: "row", gridGap: 16 }}
              onChange={handleEnumRadioChange}
            >
              <FormControlLabel
                value="YES"
                control={<Radio color="primary" />}
                label="Ja"
              />
              <FormControlLabel
                value="NO"
                control={<Radio color="primary" />}
                label="Nej"
              />
              <FormControlLabel
                value="NO_NUTRITION_CLAIMS"
                control={<Radio color="primary" />}
                label="Inga näringspåståenden"
              />
              <FormControlLabel
                value={null}
                control={<Radio color="primary" />}
                label="Ej valt"
              />
            </RadioGroup>
            <MissingFieldHint field="qrNutritionClaimsCode" />
          </FormControl>)}
          {fields.qrHealthClaimsCode && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
            <FormLabel
              required={!!requiredFields.qrHealthClaimsCode}
              classes={{ root: classes.label }}
            > 
              Är eventuella hälsopåståenden i enlighet med kraven i förordning <a target="_blank" rel="noopener noreferrer" href="https://kontrollwiki.livsmedelsverket.se/artikel/470/halsopastaenden">(EG) 1924/2006</a>?
            </FormLabel>
            <RadioGroup
              id="qrHealthClaimsCode"
              value={state?.qrHealthClaimsCode}
              name="qrHealthClaimsCode"
              style={{ flexDirection: "row", gridGap: 16 }}
              onChange={handleEnumRadioChange}
            >
              <FormControlLabel
                value="YES"
                control={<Radio color="primary" />}
                label="Ja"
              />
              <FormControlLabel
                value="NO"
                control={<Radio color="primary" />}
                label="Nej"
              />
              <FormControlLabel
                value="NO_HEALTH_CLAIMS"
                control={<Radio color="primary" />}
                label="Inga hälsopåståenden"
              />
              <FormControlLabel
                value={null}
                control={<Radio color="primary" />}
                label="Ej valt"
              />
            </RadioGroup>
            <MissingFieldHint field="qrHealthClaimsCode" />
          </FormControl>)}
          {fields.qrFoodLabeling && (<FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
            <FormLabel
              required={!!requiredFields.qrFoodLabeling}
              classes={{ root: classes.label }}
            > 
              Är märkningen i enlighet med förordning (EU) 1169/2011 och Livsmedelsverkets föreskrifter (<a target="_blank" rel="noopener noreferrer" href="https://kontrollwiki.livsmedelsverket.se/artikel/31/information-och-markning">LIVSFS 2014:4</a>) om livsmedelsinformation?
            </FormLabel>
            <RadioGroup
              id="qrFoodLabeling"
              value={state?.qrFoodLabeling}
              name="qrFoodLabeling"
              style={{ flexDirection: "row", gridGap: 16 }}
              onChange={handleRadioChange}
            >
              <FormControlLabel
                value={true}
                control={<Radio color="primary" />}
                label="Ja"
              />
              <FormControlLabel
                value={false}
                control={<Radio color="primary" />}
                label="Nej"
              />
              <FormControlLabel
                value={null}
                control={<Radio color="primary" />}
                label="Ej valt"
              />
            </RadioGroup>
            <MissingFieldHint field="qrFoodLabeling" />
          </FormControl>
          )}
        </>
      {fields.qrEfsaUlValues && (
        <FormControl disabled={readOnly} component="fieldset" style={{width: '100%'}}>
          {/* {setShowQrHeader(true)} */}
          <FormLabel
            required={!!requiredFields.qrEfsaUlValues}
            classes={{ root: classes.label }}
          > 
            Innehåller kosttillskottet, i rekommenderad daglig dos, vitaminer eller mineralämnen i en daglig dos som överstiger UL-värden (<a target="_blank" rel="noopener noreferrer" href="https://www.efsa.europa.eu/sites/default/files/assets/UL_Summary_tables.pdf">tolerable upper intake levels</a>) enligt Efsa?
          </FormLabel>
          <RadioGroup
            value={state?.qrEfsaUlValues}
            id="qrEfsaUlValues"
            name="qrEfsaUlValues"
            style={{ flexDirection: "row", gridGap: 16 }}
            onChange={handleRadioChange}
          >
            <FormControlLabel
              value={true}
              control={<Radio color="primary" />}
              label="Ja"
            />
            <FormControlLabel
              value={false}
              control={<Radio color="primary" />}
              label="Nej"
            />
            <FormControlLabel
              value={null}
              control={<Radio color="primary" />}
              label="Ej valt"
            />
          </RadioGroup>
          <MissingFieldHint field="qrEfsaUlValues" />
        </FormControl>
      )}
    </div>
  );
}

interface QualityReviewFieldsProps extends IQualityReviewFieldsDataProps {
  approve?: boolean;
}

const QualityReviewFields = ({ approve, state, setState, readOnly, ...props }: QualityReviewFieldsProps) => {
  return (
    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <QualityReviewFieldsBase {...props} readOnly={readOnly} state={state} setState={setState} />
      </div>
      {approve && state?.approve?.find(x => x.field === 'qualityReview') && (
        <ThreeWaySwitch
          readOnly={readOnly}
          state={state}
          setState={setState}
          name={"qualityReview" as keyof IProduct}
        />
      )}
    </div>
  );
}

export default QualityReviewFields;
